<template>
  <div class="push-delivery-kpis">
    <div class="vx-card p-8 w-full">
      <div class="flex flex-row flex-wrap justify-between items-end mb-8 gap-4">
        <div>
          <h3 class="mb-2">{{ $t('admin.push-delivery-kpis.title') }}</h3>
          <p class="text-sm text-gray-600 mb-0">{{ $t('admin.push-delivery-kpis.subtitle') }}</p>
        </div>
      </div>

      <div class="vx-row mb-8">
        <div class="vx-col w-full sm:w-1/2 md:w-1/4 mb-4">
          <label class="vs-input--label">{{ $t('admin.push-delivery-kpis.from') }}</label>
          <input
            v-model="fromDate"
            class="vs-inputx vs-input--input normal w-full border rounded px-3 py-2"
            type="date"
          >
        </div>
        <div class="vx-col w-full sm:w-1/2 md:w-1/4 mb-4">
          <label class="vs-input--label">{{ $t('admin.push-delivery-kpis.to') }}</label>
          <input
            v-model="toDate"
            class="vs-inputx vs-input--input normal w-full border rounded px-3 py-2"
            type="date"
          >
        </div>
        <div class="vx-col w-full sm:w-1/2 md:w-1/4 mb-4 flex items-end">
          <button
            class="sk-btn sk-btn-primary rounded-lg mr-2"
            :disabled="!canApplyFilters"
            @click="applyFilters"
          >
            {{ $t('admin.push-delivery-kpis.apply') }}
          </button>
          <button
            class="sk-btn sk-btn-outline rounded-lg"
            :disabled="!canExport || exportLoading"
            @click="exportCsv"
          >
            {{ $t('admin.push-delivery-kpis.exportCsv') }}
          </button>
        </div>
      </div>

      <p v-if="validationMessage" class="text-danger mb-4">{{ validationMessage }}</p>

      <div v-if="loadError" class="text-danger mb-4">{{ loadError }}</div>

      <div v-if="platformGlobal && !loadError" class="vx-row mb-8">
        <div class="vx-col w-full">
          <h4 class="mb-4">{{ $t('admin.push-delivery-kpis.platformKpis') }}</h4>
          <div class="flex flex-wrap gap-4">
            <div
              v-for="item in platformGlobalEntries"
              :key="item.key"
              class="border rounded p-3 min-w-40"
            >
              <div class="text-xs text-gray-600">{{ item.label }}</div>
              <div class="font-semibold">{{ item.display }}</div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="rows.length || (lastFetchOk && rows.length === 0)" class="vx-row">
        <div class="vx-col w-full overflow-x-auto">
          <SKDataTable
            :key="dataTableKey"
            :title="$t('admin.push-delivery-kpis.campaigns')"
            :stripe="true"
            :draggable="false"
            :sst="true"
            :config="datatableConfig"
            :elements="rows"
            :pagination="pagination"
            pagination-position="bottom"
            @filter="handleDataTableFilter"
          />
          <p v-if="lastFetchOk && rows.length === 0" class="text-center text-gray-600 mt-4 mb-0">
            {{ $t('admin.push-delivery-kpis.empty') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import { saveAs } from 'file-saver'
import loader from '@/modules/Shared/Mixins/loader'
import SKDataTable from '@/modules/Shared/Components/table/SKDataTable.vue'
import PushDeliveryMetricsService from '@/modules/Admin/Services/PushDeliveryMetricsService'
import { defaultDateRangeUtc, validateDeliveryMetricsDateRange } from '@/modules/Admin/Helpers/pushDeliveryMetricsDateRange'
import { mapGetters } from 'vuex'

/**
 * API `platform_global` field name -> i18n path suffix under `admin.push-delivery-kpis.*` (filled by Lokalise).
 */
const PLATFORM_GLOBAL_I18N_SUFFIX = {
  campaigns_count: 'kpi.campaignsCount',
  sent_messages_total: 'kpi.sentTotal',
  overlapped_campaigns_count: 'kpi.overlappedCampaigns',
  overlap_pairs_count: 'kpi.overlapPairs',
  sent_messages_avg: 'kpi.sentAvg',
  delivery_time_seconds_avg: 'kpi.deliverySecondsAvg',
  ttfm_seconds_avg: 'kpi.ttfmAvg',
  total_delivery_time_seconds_avg: 'kpi.totalDeliveryAvg',
  messages_per_second_avg: 'kpi.mpsAvg',
  overlap_count_avg: 'kpi.overlapAvg'
}

/** Global KPI fields shown as whole counts (grouping only, no fraction digits). */
const GLOBAL_KPI_INTEGER_KEYS = new Set(['campaigns_count', 'overlapped_campaigns_count'])

/** Global KPI averages expressed in seconds (whole seconds, no fraction digits). */
const GLOBAL_KPI_SECONDS_KEYS = new Set([
  'delivery_time_seconds_avg',
  'ttfm_seconds_avg',
  'total_delivery_time_seconds_avg'
])

export default {
  name: 'PushDeliveryKpisPage',
  components: { SKDataTable },
  mixins: [loader],
  data () {
    const { fromDate, toDate } = defaultDateRangeUtc()
    return {
      fromDate,
      toDate,
      activeFrom: fromDate,
      activeTo: toDate,
      rows: [],
      platformGlobal: null,
      pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 20,
        total: 0
      },
      loading: false,
      exportLoading: false,
      loadError: null,
      lastFetchOk: false,
      validationCode: null,
      dataTableKey: 0,
      datatableConfig: []
    }
  },
  created () {
    this.datatableConfig = this.buildDatatableConfig()
  },
  computed: {
    ...mapGetters(['AppActiveUser', 'spotlioLanguages']),
    validation () {
      return validateDeliveryMetricsDateRange(this.fromDate, this.toDate)
    },
    canApplyFilters () {
      return this.validation.valid && !this.loading
    },
    canExport () {
      return validateDeliveryMetricsDateRange(this.activeFrom, this.activeTo).valid
    },
    validationMessage () {
      if (!this.validationCode) {
        return ''
      }
      return this.$t(`admin.push-delivery-kpis.errors.${this.validationCode}`)
    },
    platformGlobalEntries () {
      if (!this.platformGlobal) {
        return []
      }
      return Object.keys(PLATFORM_GLOBAL_I18N_SUFFIX)
        .filter((k) => this.platformGlobal[k] !== undefined && this.platformGlobal[k] !== null)
        .map((key) => ({
          key,
          label: this.$t(`admin.push-delivery-kpis.${PLATFORM_GLOBAL_I18N_SUFFIX[key]}`),
          display: this.formatKpiValue(key, this.platformGlobal[key])
        }))
    }
  },
  watch: {
    '$i18n.locale' () {
      this.datatableConfig = this.buildDatatableConfig()
    },
    fromDate () { this.validationCode = null },
    toDate () { this.validationCode = null }
  },
  async mounted () {
    await this.fetchPage(1)
  },
  methods: {
    buildDatatableConfig () {
      const col = (key) => this.$t(`admin.push-delivery-kpis.col.${key}`)
      return [
        { field: 'title', head: col('title'), searchable: false, sortable: false },
        { field: 'application_display', head: col('application'), searchable: false, sortable: false },
        { field: 'to_be_delivered_display', head: col('toBeDelivered'), searchable: false, sortable: false },
        { field: 'first_send_display', head: col('firstSend'), searchable: false, sortable: false },
        { field: 'last_send_display', head: col('lastSend'), searchable: false, sortable: false },
        { field: 'sent_messages', head: col('sent'), searchable: false, sortable: false },
        { field: 'delivery_seconds_display', head: col('deliverySeconds'), searchable: false, sortable: false },
        { field: 'ttfm_display', head: col('ttfm'), searchable: false, sortable: false },
        { field: 'total_delivery_display', head: col('totalDelivery'), searchable: false, sortable: false },
        { field: 'mps_display', head: col('mps'), searchable: false, sortable: false },
        { field: 'overlap_count', head: col('overlap'), searchable: false, sortable: false }
      ]
    },
    mapRowForTable (row) {
      return {
        ...row,
        application_display: row.application_name || '—',
        to_be_delivered_display: this.formatIso(row.to_be_delivered_at),
        first_send_display: this.formatIso(row.first_send_at),
        last_send_display: this.formatIso(row.last_send_at),
        sent_messages: this.formatNumericCell(row.sent_messages, 0),
        delivery_seconds_display: this.formatNumericCell(row.delivery_time_seconds, 0),
        ttfm_display: this.formatNumericCell(row.ttfm_seconds, 0),
        total_delivery_display: this.formatNumericCell(row.total_delivery_time_seconds, 0),
        mps_display: this.formatNumericCell(row.messages_per_second, 2),
        overlap_count: this.formatNumericCell(row.overlap_count, 0)
      }
    },
    formatIso (v) {
      if (!v) {
        return '—'
      }
      const dt = DateTime.fromISO(v, { setZone: true })
      return dt.isValid ? dt.toFormat('yyyy-MM-dd HH:mm') : String(v)
    },
    formatNumericCell (v, fractionDigits = 2) {
      if (v === null || v === undefined) {
        return '—'
      }
      const n = Number(v)
      if (!Number.isFinite(n)) {
        return '—'
      }
      return this.formatGlobalKpiNumber(n, fractionDigits)
    },
    localeForKpiNumbers () {
      const currentLang = this.AppActiveUser && this.AppActiveUser.language
        ? this.AppActiveUser.language
        : null
      const spotlioLanguages = this.spotlioLanguages ? this.spotlioLanguages : []

      if (currentLang && Array.isArray(spotlioLanguages)) {
        const found = spotlioLanguages.find(l => l.acronym === currentLang)
        if (found && found.default_locale) {
          return found.default_locale.replace('_', '-')
        }
      }

      return 'en-GB'
    },
    formatGlobalKpiNumber (v, fractionDigits = 2) {
      const n = Number(v)
      if (!Number.isFinite(n)) {
        return String(v)
      }
      return new Intl.NumberFormat(this.localeForKpiNumbers(), {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits
      }).format(n)
    },
    formatKpiValue (key, v) {
      if (v === null || v === undefined) {
        return '—'
      }
      const fractionDigits = (GLOBAL_KPI_INTEGER_KEYS.has(key) || GLOBAL_KPI_SECONDS_KEYS.has(key)) ? 0 : 2
      if (typeof v === 'number') {
        return this.formatGlobalKpiNumber(v, fractionDigits)
      }
      if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) {
        return this.formatGlobalKpiNumber(v, fractionDigits)
      }
      return String(v)
    },
    async applyFilters () {
      const v = validateDeliveryMetricsDateRange(this.fromDate, this.toDate)
      if (!v.valid) {
        this.validationCode = v.code
        return
      }
      this.validationCode = null
      this.activeFrom = this.fromDate
      this.activeTo = this.toDate
      this.dataTableKey += 1
      this.datatableConfig = this.buildDatatableConfig()
      await this.fetchPage(1)
    },
    async handleDataTableFilter (params) {
      await this.fetchPage(params && params.page ? params.page : 1)
    },
    async fetchPage (page) {
      const range = validateDeliveryMetricsDateRange(this.activeFrom, this.activeTo)
      if (!range.valid) {
        const errPath = `admin.push-delivery-kpis.errors.${range.code}`
        this.loadError = this.$te(errPath) ? this.$t(errPath) : this.$t('admin.push-delivery-kpis.loadFailed')
        return
      }
      this.loading = true
      this.loadError = null
      try {
        await this.load(async () => {
          const obj = await PushDeliveryMetricsService.fetchMetrics({
            from: this.activeFrom,
            to: this.activeTo,
            groupBy: 'platform',
            page,
            perPage: this.pagination.per_page
          })
          this.platformGlobal = obj.platform_global || null
          const raw = Array.isArray(obj.data) ? obj.data : []
          this.rows = raw.map((r) => this.mapRowForTable(r))
          this.pagination = {
            current_page: obj.current_page || 1,
            last_page: obj.last_page || 1,
            per_page: obj.per_page || 20,
            total: obj.total || 0
          }
          this.lastFetchOk = true
        })
      } catch (e) {
        this.lastFetchOk = false
        this.rows = []
        this.platformGlobal = null
        this.loadError = this.$t('admin.push-delivery-kpis.loadFailed')
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    async exportCsv () {
      const range = validateDeliveryMetricsDateRange(this.activeFrom, this.activeTo)
      if (!range.valid) {
        this.validationCode = range.code
        return
      }
      this.exportLoading = true
      try {
        const response = await PushDeliveryMetricsService.downloadExport({
          from: this.activeFrom,
          to: this.activeTo
        })
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' })
        let filename = 'push-delivery-metrics.csv'
        const cd = response.headers && (response.headers['content-disposition'] || response.headers['Content-Disposition'])
        if (cd) {
          const m = /filename="([^"]+)"/.exec(cd)
          if (m && m[1]) {
            filename = m[1]
          }
        }
        saveAs(blob, filename)
      } catch (e) {
        console.error(e)
        this.$vs.notify({
          color: 'danger',
          title: this.$t('shops.error.0'),
          text: this.$t('admin.push-delivery-kpis.exportFailed')
        })
      } finally {
        this.exportLoading = false
      }
    }
  }
}
</script>
